import { Injectable } from '@angular/core';
import { HttpService } from '@app/core/services/http';
import { Observable, throwError, of, Subject, BehaviorSubject } from 'rxjs';
import { AoiSizeEstimatePayload, ChartEntitlementsUpdateDataPayload } from '.';
import { areaOfInterestType, endpoints } from '@app/constants';
import { mergeMap, catchError } from 'rxjs/operators';
import { CardModel, CardsData } from './my-charts-list/my-charts-list.models';
import { MyChartItemChartData } from '@app/core/features/charts-manager/my-charts/my-chart-item';

@Injectable({
  providedIn: 'root'
})
export class MyChartsService {
  public checkChangedMyListCharts = new Subject<number>();
  public chartPreparing$ = new BehaviorSubject<CardModel[]>([]);
  public chartReady$ = new BehaviorSubject<CardModel[]>([]);
  public checkChangedActualSize = new Subject<boolean>();

  public checkChangedChartData = new BehaviorSubject<MyChartItemChartData[]>([]);

  private chartPreparing = [];
  private chartReady = [];

  public checkChangedLanguage = new Subject<number>();

  constructor(
    private http: HttpService
  ) { }

  getAoiSizeEstimate = (payload: AoiSizeEstimatePayload): Observable<object> => {
    return this.http
      .post({ url: endpoints.getAoiSizeEstimate, body: payload})
      .pipe(
        mergeMap(response => of(response)),
        catchError((error) => throwError(error))
      );
  }

  setChartPreparingItem(cardData: CardModel) {
    const index = this.chartPreparing.findIndex(item => item.lighthouseId === cardData.lighthouseId);
    if (index === -1) {
      this.chartPreparing.push(cardData);
      this.chartPreparing$.next(this.chartPreparing);
    }
  }

  removeChartPreparingItem(lighthouseId: string) {
    const index = this.chartPreparing.findIndex(item => item.lighthouseId === lighthouseId);
    if (index !== -1) {
      this.chartPreparing.splice(index, 1);
    }
  }

  setChartReadyItem(cardData: CardModel) {
    const index = this.chartReady.findIndex(item => item.lighthouseId === cardData.lighthouseId);
    if (index === -1 ) {
      this.chartReady.push(cardData);
      this.chartReady$.next(this.chartReady);
    }
    this.removeChartPreparingItem(cardData.lighthouseId);
  }

  removeChartReadyItem(lighthouseId: string) {
    const index = this.chartReady.findIndex(item => item.lighthouseId === lighthouseId);
    if (index !== -1) {
      this.chartReady.splice(index, 1);
    }
  }

  updateChartEntitlementsData = (payload: ChartEntitlementsUpdateDataPayload): Observable<object> => {
    return this.http
      .post({ url: endpoints.updateChartEntitlementsData, body: payload})
      .pipe(
        mergeMap(response => of(response)),
        catchError((error) => throwError(error))
      );
  }

  toggleIncludeDownload = (chartEntitlementId: string): Observable<object> => {
    return this.http
      .post({ url: endpoints.toggleIncludeDownload.replace('{id}', chartEntitlementId) })
      .pipe(
        mergeMap(response => of(response)),
        catchError((error) => throwError(error))
      );
  }

  getProductData = (productId: string): Observable<object> => {
    return this.http
      .get({ url: endpoints.getProduct + productId })
      .pipe(
        mergeMap(response => of(response)),
        catchError((error) => throwError(error))
      );
  }

  aoiHasUpdate = (chartEntitlements: CardsData[]): boolean => {
    return chartEntitlements.some(x => {
      if (!x.isExpired && x.isUpdateAvailable && x.isIncludeInDownload) {
        const chart = x.areaOfInterestDtos
          ? x.areaOfInterestDtos.filter(item => item.areaOfInterestType === areaOfInterestType.CHART_DATA)
          : [];

        return chart.filter(y => y.lastDownloadedProductVersion !== x.version).length;
      }
    });
  }
}
